/**
 * Tiny seeded PRNG for deterministic tests and reproducible AI walks.
 * Uses mulberry32 — small, fast, good-enough distribution for game logic.
 */

export interface Rng {
  /** Returns a float in [0, 1). */
  next(): number;
  /** Integer in [0, max). */
  int(max: number): number;
  /** Integer in [min, max] inclusive. */
  range(min: number, max: number): number;
  /** Pick one element of an array uniformly. Returns undefined for empty input. */
  pick<T>(arr: readonly T[]): T | undefined;
}

export function makeRng(seed: number): Rng {
  let state = seed | 0;
  const next = (): number => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
  return {
    next,
    int(max: number): number {
      return Math.floor(next() * max);
    },
    range(min: number, max: number): number {
      return min + Math.floor(next() * (max - min + 1));
    },
    pick<T>(arr: readonly T[]): T | undefined {
      if (arr.length === 0) return undefined;
      return arr[Math.floor(next() * arr.length)];
    },
  };
}
